import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface SlideData {
  _id: string;
  priority: number;
  title?: string;
  description?: string;
  file: {
    fileKey: string;
    fileType: string;
    featured: boolean;
    fileURL: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface IntroSliderProps {
  slides: SlideData[];
  onComplete?: () => void;
  onSkip?: () => void;
}

const IntroSlider: React.FC<IntroSliderProps> = ({ slides, onComplete, onSkip }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sort slides by priority
  const sortedSlides = [...slides].sort((a, b) => a.priority - b.priority);

  const handleNext = () => {
    if (currentSlide < sortedSlides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete?.();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onSkip?.();
  };


  // Touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < sortedSlides.length - 1) {
      handleNext();
    }
    if (isRightSwipe && currentSlide > 0) {
      handlePrevious();
    }
  };

  const currentSlideData = sortedSlides[currentSlide];
  const totalSlides = sortedSlides.length;

  if (!currentSlideData) {
    return null;
  }

  return (
    <Box
      sx={{
        // position: 'relative',
        // width: '100%',
        height: '100vh',
        backgroundColor: '#2C2C2C',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        px: isMobile ? 2 : 8,
        py: isMobile ? 2 : 6,
      }}
    >
      <Box
        sx={{
          width: '100%',
          // maxWidth: 420,
          height: isMobile ? '100%' : '720px',
          maxHeight: '100%',
          backgroundColor: '#FFFFFF',
          // borderRadius: 32,
          // boxShadow: '0 30px 80px rgba(0, 0, 0, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Skip Button */}
        <Box
          sx={{
            position: 'absolute',
            top: 18,
            right: 18,
            zIndex: 10,
          }}
        >
          <Button
            onClick={handleSkip}
            sx={{
              backgroundColor: '#FFF3E6',
              color: '#2C2C2C',
              borderRadius: 20,
              px: 2,
              py: 0.5,
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: '0 8px 18px rgba(0, 0, 0, 0.12)',
              '&:hover': {
                backgroundColor: '#FFE7CE',
              },
            }}
          >
            Skip
          </Button>
        </Box>

        {/* Touch Area for Swipe Navigation */}
        <Box
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 5,
            cursor: isMobile ? 'grab' : 'default',
            '&:active': {
              cursor: isMobile ? 'grabbing' : 'default',
            },
          }}
        />

        {/* Image */}
        <Box
          sx={{
            width: '100%',
            flexGrow: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            backgroundImage: `url(${currentSlideData.file.fileURL})`,
            backgroundSize: 'cover',
            // backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Slide Indicators */}
          <Box
            sx={{
              position: 'absolute',
              bottom: isMobile ? 16 : 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              borderRadius: 999,
              backdropFilter: 'blur(8px)',
            }}
          >
            {sortedSlides.map((slide, index) => (
              <Box
                key={slide._id || index}
                sx={{
                  width: index === currentSlide ? 12 : 8,
                  height: index === currentSlide ? 12 : 8,
                  borderRadius: '50%',
                  backgroundColor:
                    index === currentSlide ? '#FFFFFF' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Description + Indicators */}
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#FFF3E6',
            py: 4,
            // px: 4,
            display: 'flex',
            // flexDirection: 'column',
            alignItems: 'center',
            // gap: 3,
            color: '#2C2C2C',
          }}
        >
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            {currentSlideData.title && (
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  letterSpacing: 0.2,
                }}
              >
                {currentSlideData.title}
              </Typography>
            )}
            {currentSlideData.description && (
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? '15px' : '17px',
                  lineHeight: 1.6,
                  opacity: 0.9,
                }}
              >
                {currentSlideData.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Bottom Next Button */}
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            py: 4,
            // px: 4,
          }}
        >
          <Button
            onClick={handleNext}
            sx={{
              backgroundColor: '#FF7A31',
              color: '#FFFFFF',
              borderRadius: 24,
              py: 1.6,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              width: '100%',
              boxShadow: '0 8px 24px rgba(255, 122, 49, 0.35)',
              border: 'none',
              '&:hover': {
                backgroundColor: '#E96C27',
                boxShadow: '0 10px 28px rgba(233, 108, 39, 0.45)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            {currentSlide === totalSlides - 1 ? 'Get Started' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default IntroSlider;
